import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import anime from 'animejs';
import styled from 'styled-components';

const StyledLoader = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: #050508;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  overflow: hidden;

  /* Scanlines effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(rgba(255, 42, 42, 0.08) 50%, rgba(0, 0, 0, 0.6) 50%);
    background-size: 100% 4px;
    z-index: 2;
    pointer-events: none;
  }

  /* Vignette effect */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, transparent 30%, rgba(0, 0, 0, 0.9) 100%);
    z-index: 3;
    pointer-events: none;
  }

  .hud-container {
    position: relative;
    z-index: 5;
    width: clamp(280px, 80vmin, 600px);
    display: flex;
    flex-direction: column;
    gap: clamp(10px, 3vmin, 20px);
    padding: clamp(10px, 4vmin, 30px);
    align-items: center;
    animation: soft-flicker 4s infinite;
  }

  .hud-header {
    width: 100%;
    color: #ff2a2a;
    font-size: clamp(9px, 2vmin, var(--fz-xs));
    letter-spacing: clamp(1px, 0.3vmin, 2px);
    display: flex;
    justify-content: space-between;
    opacity: 0.8;
  }

  .hex-wrapper {
    display: flex;
    justify-content: center;
    margin: 10px 0;
  }

  .hex-svg {
    width: clamp(80px, 20vmin, 150px);
    height: clamp(80px, 20vmin, 150px);
  }

  .hex-outer {
    transform-origin: center;
    animation: rotate-cw 4s linear infinite;
    stroke-dasharray: 20, 10;
  }

  .hex-inner {
    transform-origin: center;
    animation: rotate-ccw 2.5s linear infinite;
    stroke-dasharray: 10, 5;
    opacity: 0.7;
  }

  .hex-center {
    transform-origin: center;
    animation: pulse 1.5s ease-in-out infinite;
  }

  .hud-frame {
    width: 100%;
    position: relative;
  }

  .bar-svg {
    width: 100%;
    height: auto;
  }

  .hud-footer {
    width: 100%;
    display: flex;
    justify-content: space-between;
    color: #fff;
    font-size: clamp(10px, 2.2vmin, var(--fz-sm));
    letter-spacing: 1px;
  }

  .loader-text {
    color: #ff2a2a;
    font-size: clamp(14px, 3vmin, var(--fz-md));
    letter-spacing: clamp(1px, 0.4vmin, 2px);
    animation: flicker 2s infinite;
    text-shadow: 0 0 10px rgba(255, 42, 42, 0.5);
  }

  .percent {
    color: #ff2a2a;
    font-weight: bold;
  }

  .blink {
    animation: blink 1s steps(1) infinite;
  }

  @keyframes rotate-cw {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes rotate-ccw {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(-360deg);
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(0.9);
      opacity: 0.3;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.8;
    }
    100% {
      transform: scale(0.9);
      opacity: 0.3;
    }
  }

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }

  @keyframes soft-flicker {
    0%,
    100% {
      opacity: 1;
    }
    10% {
      opacity: 0.3;
    }
    11% {
      opacity: 1;
    }
    30% {
      opacity: 1;
    }
    31% {
      opacity: 0.4;
    }
    32% {
      opacity: 1;
    }
    50% {
      opacity: 1;
    }
    51% {
      opacity: 0.2;
    }
    52% {
      opacity: 1;
    }
    70% {
      opacity: 1;
    }
    71% {
      opacity: 0.3;
    }
    72% {
      opacity: 1;
    }
  }

  @keyframes flicker {
    0%,
    100% {
      opacity: 1;
      text-shadow: 0 0 10px #ff2a2a;
    }
    10% {
      opacity: 0.3;
      text-shadow: none;
    }
    11% {
      opacity: 1;
      text-shadow: 0 0 10px #ff2a2a;
    }
    30% {
      opacity: 1;
    }
    31% {
      opacity: 0.2;
      text-shadow: none;
    }
    32% {
      opacity: 1;
      text-shadow: 0 0 10px #ff2a2a;
    }
    70% {
      opacity: 1;
    }
    71% {
      opacity: 0.4;
      text-shadow: none;
    }
    72% {
      opacity: 1;
      text-shadow: 0 0 10px #ff2a2a;
    }
  }
`;

const Loader = ({ finishLoading }) => {
  const [isMounted, setIsMounted] = useState(false);

  const animate = () => {
    const progressDuration = 2500;
    const fadeOutDelay = 500;

    const loader = anime.timeline({
      complete: () => finishLoading(),
    });

    loader
      .add({
        targets: '.hud-container',
        opacity: [0, 1],
        scale: [0.9, 1],
        duration: 500,
        easing: 'easeOutExpo',
      })
      .add({
        targets: '.progress-fill',
        scaleX: [0, 1],
        duration: progressDuration,
        easing: 'easeInOutQuad',
      })
      .add(
        {
          targets: '.percent-num',
          innerHTML: [0, 100],
          round: 1,
          duration: progressDuration,
          easing: 'easeInOutQuad',
        },
        `-=${progressDuration}`,
      )
      .add({
        targets: '.loader',
        duration: 600,
        easing: 'easeInOutQuart',
        opacity: 0,
        zIndex: -1,
        delay: fadeOutDelay,
      });
  };

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 10);
    animate();
    return () => clearTimeout(timeout);
  }, []);

  return (
    <StyledLoader className="loader" isMounted={isMounted}>
      <Helmet bodyAttributes={{ class: `hidden` }} />
      <div className="hud-container">
        <div className="hud-header">
          <span>
            <span className="blink">&gt;</span> LOADING_PROTOCOL
          </span>
          <span>SYS_VER_1.0</span>
        </div>

        <div className="hex-wrapper">
          <svg viewBox="0 0 100 100" className="hex-svg">
            <polygon
              points="50,5 89,27 89,73 50,95 11,73 11,27"
              fill="none"
              stroke="#ff2a2a"
              strokeWidth="1.5"
              className="hex-outer"
            />
            <polygon
              points="50,20 76,35 76,65 50,80 24,65 24,35"
              fill="none"
              stroke="#ff2a2a"
              strokeWidth="1"
              className="hex-inner"
            />
            <circle cx="50" cy="50" r="5" fill="#ff2a2a" className="hex-center" />
          </svg>
        </div>

        <div className="hud-frame">
          <svg viewBox="0 0 400 50" className="bar-svg">
            <path
              d="M5,5 L395,5 L395,35 L380,45 L5,45 Z"
              fill="none"
              stroke="#ff2a2a"
              strokeWidth="1.5"
              opacity="0.8"
            />
            <path d="M12,12 L383,12 L383,33 L373,40 L12,40 Z" fill="rgba(255, 42, 42, 0.03)" />
            <path
              d="M12,12 L383,12 L383,33 L373,40 L12,40 Z"
              fill="#ff2a2a"
              className="progress-fill"
              style={{ transformOrigin: 'left', transform: 'scaleX(0)' }}
            />
          </svg>
        </div>

        <div className="hud-footer">
          <span className="loader-text">NOOR UL BASEER</span>
          <span className="percent">
            <span className="percent-num">0</span>%
          </span>
        </div>
      </div>
    </StyledLoader>
  );
};

Loader.propTypes = {
  finishLoading: PropTypes.func.isRequired,
};

export default Loader;
